import React, { useState, useRef, useEffect, useCallback } from 'react';
import Eye from 'lucide-react/dist/esm/icons/eye';
import Search from 'lucide-react/dist/esm/icons/search';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import { Avatar, Spinner, Text } from '../../../components/ui';
import { searchPlatformUsers, type UserSearchResult } from '../../../services/api/platform';
import { useImpersonationStore } from '../../../stores';
import { useToast } from '../../../components/feedback/Toast';

const MIN_QUERY = 2;
const DEBOUNCE_MS = 300;

const ImpersonationSelector: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<UserSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  const { startImpersonation, loading: impersonating } = useImpersonationStore();
  const toast = useToast();

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
      setHighlightIdx(-1);
    }
  }, [open]);

  const doSearch = useCallback(async (q: string) => {
    if (q.length < MIN_QUERY) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const data = await searchPlatformUsers(q);
      setResults(data);
      setHighlightIdx(-1);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => doSearch(val), DEBOUNCE_MS);
  };

  const handleSelect = async (user: UserSearchResult) => {
    try {
      await startImpersonation(user.id, user.display_name, user.email, user.org_name);
    } catch (err: any) {
      const msg = err?.response?.data?.detail || 'Impersonation failed';
      toast.error(msg);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIdx >= 0 && results[highlightIdx]) {
      e.preventDefault();
      handleSelect(results[highlightIdx]);
    }
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all duration-200
          bg-gradient-to-r from-rose-500/20 to-pink-500/20 border border-rose-500/30 text-rose-300
          hover:from-rose-500/30 hover:to-pink-500/30 hover:border-rose-500/40"
        title="Impersonate user"
      >
        <Eye size={14} />
        <span className="hidden sm:inline">View as</span>
      </button>

      {open && (
        <div className="absolute top-[calc(100%+0.5rem)] right-0 w-[340px] rounded-xl bg-surface-page border border-subtle shadow-xl z-dropdown animate-fade-in-down">
          <div className="p-3 border-b border-subtle">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-content-tertiary" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder="Search users by name or email..."
                className="w-full pl-9 pr-3 py-2 rounded-lg bg-surface-primary border border-subtle text-content text-sm placeholder:text-content-muted focus:outline-none focus:border-brand focus:ring-1 focus:ring-brand/30 transition-colors"
                disabled={impersonating}
              />
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {searching && (
              <div className="flex items-center justify-center py-6">
                <Spinner size="sm" />
              </div>
            )}

            {!searching && query.length >= MIN_QUERY && results.length === 0 && (
              <div className="py-6 text-center">
                <Text variant="caption" className="text-content-muted">No users found</Text>
              </div>
            )}

            {!searching && query.length > 0 && query.length < MIN_QUERY && (
              <div className="py-6 text-center">
                <Text variant="caption" className="text-content-muted">Type at least {MIN_QUERY} characters</Text>
              </div>
            )}

            {!searching && results.map((user, idx) => (
              <button
                key={user.id}
                onClick={() => handleSelect(user)}
                disabled={impersonating}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors duration-100
                  hover:bg-surface-primary ${idx === highlightIdx ? 'bg-surface-primary' : ''}
                  ${idx < results.length - 1 ? 'border-b border-subtle/50' : ''}
                  disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                <Avatar name={user.display_name || user.email} size="sm" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-content truncate">
                    {user.display_name || user.email}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    {user.org_name && (
                      <>
                        <Building2 size={10} className="text-content-muted shrink-0" />
                        <span className="text-2xs text-content-tertiary truncate">{user.org_name}</span>
                        <span className="text-2xs text-content-muted">·</span>
                      </>
                    )}
                    <span className="text-2xs text-content-muted truncate">{user.email}</span>
                  </div>
                </div>
              </button>
            ))}

            {!searching && query.length === 0 && (
              <div className="py-6 text-center">
                <Text variant="caption" className="text-content-muted">Start typing to search users</Text>
              </div>
            )}
          </div>

          {impersonating && (
            <div className="flex items-center justify-center gap-2 px-3 py-2.5 border-t border-subtle bg-surface-primary/50">
              <Spinner size="sm" />
              <Text variant="caption" className="text-content-secondary">Switching user...</Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(ImpersonationSelector);
